import { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  COOKIE_CONSENT_VERSION,
  hasOptionalTrackingServices,
  readStoredCookieConsent,
  writeStoredCookieConsent,
} from "../lib/cookieConsent";
import { shutdownPostHog } from "../lib/posthogConfig";
import { removeTawkScript } from "../lib/tawkToLoader";

const CookieConsentContext = createContext(null);

function applyConsentSideEffects(nextConsent) {
  if (!nextConsent?.analytics) {
    shutdownPostHog();
  }
  if (!nextConsent?.support) {
    removeTawkScript();
  }
}

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => readStoredCookieConsent());
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const persistConsent = useCallback(({ analytics, support }) => {
    const next = writeStoredCookieConsent({ analytics, support });
    applyConsentSideEffects(next);
    setConsent(next);
    setPreferencesOpen(false);
    return next;
  }, []);

  const acceptAll = useCallback(() => {
    persistConsent({ analytics: true, support: true });
  }, [persistConsent]);

  const rejectNonEssential = useCallback(() => {
    persistConsent({ analytics: false, support: false });
  }, [persistConsent]);

  const savePreferences = useCallback(
    ({ analytics, support }) => {
      persistConsent({ analytics, support });
    },
    [persistConsent]
  );

  const openPreferences = useCallback(() => {
    setPreferencesOpen(true);
  }, []);

  const closePreferences = useCallback(() => {
    setPreferencesOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      consentVersion: COOKIE_CONSENT_VERSION,
      consent,
      hasDecision: Boolean(consent),
      showInitialBanner: hasOptionalTrackingServices() && !consent,
      preferencesOpen,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      openPreferences,
      closePreferences,
    }),
    [
      acceptAll,
      closePreferences,
      consent,
      openPreferences,
      preferencesOpen,
      rejectNonEssential,
      savePreferences,
    ]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error("useCookieConsent must be used within CookieConsentProvider");
  }
  return context;
}

/** Safe outside the provider — returns null when unavailable. */
export function useCookieConsentOptional() {
  return useContext(CookieConsentContext);
}
